import type { ChartData } from 'chart.js';
import { useMemo } from 'react';

import { Span } from '@/components/Typography/Typo';
import {
  InsightExplorerTrendViewType,
  InsightExplorerViewType,
} from '@/features/InsightExplorer/types/constants';
import type { InsightExplorerTrendResponse } from '@/features/InsightExplorer/types/types';
import { insightExplorerUtils } from '@/features/InsightExplorer/utils/insight-explorer.utils';
import useColor from '@/hooks/domain/useColor';
import type { RangeUnit } from '@/utils/getDatePreset';
import { getFormatByRangeUnit, getTimezoneDayjs } from '@/utils/time';

import type { HierarchyNodeData } from '../../InsightExplorerSidebar/HierarchyNavigator/HierarchyNavigator';

export const useTotalVolumeTrendSection = ({
  trendViewType,
  data,
  view,
  viewType,
  selectedNode,
}: {
  trendViewType: InsightExplorerTrendViewType;
  data: InsightExplorerTrendResponse;
  view: InsightExplorerViewType;
  viewType: RangeUnit;
  selectedNode: HierarchyNodeData;
}) => {
  const color = useColor();
  const firstData = data[0];

  if (!firstData)
    throw new Error('[useTotalVolumeTrendSection]: First data does not exist');

  const negativeVolumes = firstData.negative_volume;

  const totalVolumes = firstData.volume;

  const negativeTotal = negativeVolumes.buckets.reduce(
    (acc, curr) => acc + curr.count,
    0,
  );

  const totalTotal = totalVolumes.buckets.reduce(
    (acc, curr) => acc + curr.count,
    0,
  );

  const totalVolumeChartData: ChartData<'line'> | null = useMemo(() => {
    const labels = totalVolumes.buckets.map((bucket) => {
      const date = getTimezoneDayjs(bucket.bucket.toString());
      const formattedDate = getFormatByRangeUnit(viewType)(date);
      return formattedDate;
    });

    if (view === InsightExplorerViewType.SENTIMENTS) {
      if (trendViewType === InsightExplorerTrendViewType.SENTIMENT_SCORE) {
        const scoreTrends = totalVolumes.buckets.map((volume, index) => {
          return insightExplorerUtils.calcSentimentScore(
            volume.count,
            negativeVolumes.buckets[index].count,
          );
        });
        return {
          labels: labels,
          datasets: [
            {
              alias: selectedNode.id,
              label: 'Sentiment Score',
              data: scoreTrends,
              borderColor: color.primary100,
              backgroundColor: color.primary100,
            },
          ],
        };
      }
      if (trendViewType === InsightExplorerTrendViewType.NEGATIVE_VOLUME) {
        return {
          labels: labels,
          datasets: [
            {
              alias: selectedNode.id,
              label: 'Negative Volume',
              data: negativeVolumes.buckets.map((bucket) => bucket.count),
              borderColor: color.red600,
              backgroundColor: color.red600,
            },
          ],
        };
      }
    }

    return {
      labels: labels,
      datasets: [
        {
          alias: selectedNode.id,
          label: 'Total Volume',
          data: totalVolumes.buckets.map((bucket) => bucket.count),
          borderColor: color.primary100,
          backgroundColor: color.primary100,
        },
      ],
    };
  }, [
    totalVolumes.buckets,
    view,
    color.primary100,
    color.red600,
    viewType,
    trendViewType,
    selectedNode.id,
    negativeVolumes.buckets,
  ]);

  const heroDisplayValue: number = useMemo(() => {
    if (view === InsightExplorerViewType.SENTIMENTS) {
      if (trendViewType === InsightExplorerTrendViewType.SENTIMENT_SCORE) {
        return insightExplorerUtils.calcSentimentScore(
          totalTotal,
          negativeTotal,
        );
      }
      if (trendViewType === InsightExplorerTrendViewType.NEGATIVE_VOLUME) {
        return negativeTotal;
      }
    }

    return totalTotal;
  }, [view, trendViewType, totalTotal, negativeTotal]);

  const heroDisplaylabel = useMemo(() => {
    if (view === InsightExplorerViewType.SENTIMENTS) {
      if (trendViewType === InsightExplorerTrendViewType.SENTIMENT_SCORE) {
        return <Span>Sentiment Score</Span>;
      }
      if (trendViewType === InsightExplorerTrendViewType.NEGATIVE_VOLUME) {
        return 'Negative Feedbacks';
      }
    }

    return 'Feedbacks';
  }, [trendViewType, view]);

  return {
    chartData: totalVolumeChartData,
    heroDisplayValue: heroDisplayValue,
    heroDisplaylabel: heroDisplaylabel,
  };
};
